import { SpanDirection } from "building-types/floors/floorComponentsTypes";
import { JoistPlacement } from "building-types/floors/floorJoistPlacementTypes";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Framing } from "building-types/framing/framingTypes";
import type {
  LumberLengthDescriptor,
  LumberPiecesByProfile,
  LumberProfileDescriptor,
  LumberRail,
  StandardLumberCounterSet,
} from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import {
  StudLumberLengths,
  JoistLumberLengths,
  getFramingProfileDescriptor,
  getLumberProfile,
  lumberPiecesByProfileNew,
  lumberRailNew,
  rectangularLumberDescriptor,
} from "doc/utils/lumberTools";
import { aabb2Area, aabb2ByPolyLine2s } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import {
  PolyLine2PolyLine2ContainmentClass,
  Vec2PolyLine2ContainmentClass,
  classifyContainmentPolyLine2PolyLine2,
  classifyContainmentVec2PolyLine2,
} from "geom/containment";
import { COMPACTNESS_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, DIST_Z_FIGHT } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { offsetPolyLine2 } from "geom/offset";
import { planeNewXY } from "geom/plane";
import {
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Close,
  polyLine2Copy,
  polyLine2CopySimplify,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeNormal,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2IsAxisAlignedRect,
  polyLine2Length,
  polyLine2Move,
  polyLine2ReverseCopy,
  polyLine2Rotate,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2Vertex,
  polyLine2VertexCount,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3OnPlane } from "geom/polyLine3";
import { vec2IsBetweenUsingLengthBasedCalc } from "geom/vec2";
import { findBestSingleLumberLength, updateLumberCounters } from "../walls/wallFramingUtils";

// define a new interface for storing rotated polygon bounding box 2d information
// this extends axis aligned bounding box in 2d
interface RPBB2 extends AABB2 {
  rot: number; //this is a clock-wise angle by which the polygon has to be rotated to fit in the bounding box
  minusArea: number; //this is the area differential between the bounding box and the polygon
  rotPoly: PolyLine2; //this is the rotated polygon, rotate by rot to get the original polygon
  rotOpenings: PolyLine2[]; //this is the rotated openings, rotate by rot to get the original openings
}
interface JoistRayOriginPoints {
  x: number;
  y: number;
  spansInXDir: boolean;
}

function findJoistLumberLength(type: string, length: number): LumberLengthDescriptor {
  if (type === JoistPlacement.RIM) {
    return findBestSingleLumberLength(StudLumberLengths, length);
  } else if (type === JoistPlacement.FLOOR) {
    return findBestSingleLumberLength(JoistLumberLengths, length);
  } else {
    return JoistLumberLengths[0];
  }
}

const SLIGHTLY_LESS_THAN_HALF = 0.5 - DIST_EPSILON;

function lumberProfileAutofilledForFloorPlate(
  baseRect: LumberProfileDescriptor,
  floorPlateThickness: number,
  height: number,
): LumberProfileDescriptor {
  return {
    ...baseRect,
    name: `${baseRect.name} (autofill)`,
    width: floorPlateThickness,
    height,
    annotationLabel: baseRect.annotationLabel,
  };
}

function dimensionalLumberProfileMatching(rect: LumberProfileDescriptor): LumberProfileDescriptor | undefined {
  return Object.values(LumberProfileCatalog).find(
    (profile) =>
      Math.abs(profile.width - rect.width) < DIST_EPSILON && Math.abs(profile.height - rect.height) < DIST_EPSILON,
  );
}

function floorLumberProfileFromFramingProfile(descriptor: LumberProfileDescriptor): LumberProfileDescriptor {
  return dimensionalLumberProfileMatching(descriptor) ?? descriptor;
}

export function makeFloorFraming({
  framingOutlines,
  floorPlateElevation,
  floorPlateThickness,
  joistComponentHeight,
  joistSpacing,
  spanDir,
  lumberCounters,
  frameFloorAssembly,
  framingComponents,
}: {
  framingOutlines: PolyLine2[];
  floorPlateElevation: number;
  floorPlateThickness: number;
  joistComponentHeight: number;
  joistSpacing: number;
  spanDir: SpanDirection;
  lumberCounters?: StandardLumberCounterSet;
  frameFloorAssembly?: FrameFloorAssemblyEntity;
  framingComponents?: Table<FramingComponentEntity>;
}): Framing[] {
  const floorFraming: Framing[] = [];

  if (floorPlateThickness < DIST_EPSILON) {
    return floorFraming;
  }

  const structureComponent: FramingComponentEntity | undefined =
    frameFloorAssembly && framingComponents ? framingComponents[frameFloorAssembly.joistComponentId] : undefined;
  const rimComponent: FramingComponentEntity | undefined =
    frameFloorAssembly && framingComponents ? framingComponents[frameFloorAssembly.rimComponentId] : undefined;

  const useAssemblyProfiles = structureComponent !== undefined && rimComponent !== undefined;

  const structureRect: LumberProfileDescriptor | undefined = useAssemblyProfiles
    ? rectangularLumberDescriptor(getFramingProfileDescriptor(structureComponent))
    : undefined;
  const structureLumberProfile: LumberProfileDescriptor | undefined = structureRect
    ? floorLumberProfileFromFramingProfile(structureRect)
    : undefined;

  const floorLayoutProfile: LumberProfileDescriptor = structureRect ?? getLumberProfile(floorPlateThickness, false);

  const joistLumberProfile: LumberProfileDescriptor = useAssemblyProfiles
    ? lumberProfileAutofilledForFloorPlate(
        structureLumberProfile ?? floorLayoutProfile,
        floorLayoutProfile.width,
        floorLayoutProfile.height,
      )
    : {
        type: floorLayoutProfile.type,
        name: `${floorLayoutProfile.name} (autofill)`,
        width: floorPlateThickness,
        height: joistComponentHeight,
        annotationLabel: floorLayoutProfile.annotationLabel,
      };

  const rimRect = useAssemblyProfiles
    ? rectangularLumberDescriptor(getFramingProfileDescriptor(rimComponent))
    : undefined;
  const rimLumberRect = rimRect ? floorLumberProfileFromFramingProfile(rimRect) : undefined;
  const rimLumberProfile: LumberProfileDescriptor =
    useAssemblyProfiles && rimRect !== undefined && rimLumberRect !== undefined
      ? lumberProfileAutofilledForFloorPlate(rimLumberRect, rimRect.width, rimRect.height)
      : joistLumberProfile;

  const rimPieces = lumberPiecesByProfileNew(rimLumberProfile);
  const floorPieces = useAssemblyProfiles ? lumberPiecesByProfileNew(joistLumberProfile) : rimPieces;

  const addLumber = (lumberPieces: LumberPiecesByProfile, type: string, lumberRail: LumberRail) => {
    const { railsByType } = lumberPieces;
    if (!railsByType[type]) {
      railsByType[type] = [];
    }
    // turned off to make baselines work for now
    railsByType[type].push(lumberRail);
  };

  const addJoist = (lumberPieces: LumberPiecesByProfile, type: JoistPlacement, rail: PolyLine2, elevation: number) => {
    const joistPlane = planeNewXY(0, 0, elevation);
    const [nx, ny] = polyLine2EdgeNormal(polyLine2ReverseCopy(rail), 0);
    const joistRail = polyLine3OnPlane(rail, joistPlane);
    addLumber(lumberPieces, type, lumberRailNew(joistRail, nx, ny, 0));
  };

  // some framing outlines are not coming in closed so closing them here.
  // may be this needs to be done someplace else?
  const framingOutlinesClosed: PolyLine2[] = [];
  for (const framingOutline of framingOutlines) {
    framingOutlinesClosed.push(polyLine2Close(framingOutline));
  }

  const groupedFramingOutlines = groupFramingOutlines(framingOutlinesClosed);

  for (const framingOutlines of groupedFramingOutlines) {
    for (const framingOutline of framingOutlines) {
      for (let i = 0, l = polyLine2EdgeCount(framingOutline); i < l; i++) {
        const rimJoistRail = polyLine2Edge(framingOutline, i);
        polyLine2Extend(rimJoistRail, -DIST_Z_FIGHT, -DIST_Z_FIGHT);
        addJoist(rimPieces, JoistPlacement.RIM, rimJoistRail, floorPlateElevation - rimLumberProfile.width);
      }
    }
  }

  for (const framingOutlines of groupedFramingOutlines) {
    // using a value slightly less than 0.5 to avoid graphical glitches
    const optimalBB = computeOptimalBoundingBox(framingOutlines, SLIGHTLY_LESS_THAN_HALF * floorLayoutProfile.height);
    const jstCriticalPts = computeJoistsRayOriginPointsForFramingCalc(optimalBB, spanDir);
    const floorJoists = computeFloorJoists(
      optimalBB,
      jstCriticalPts,
      joistSpacing,
      floorLayoutProfile.width,
      floorLayoutProfile.height,
    );
    for (const floorJoist of floorJoists) {
      for (let i = 0, l = polyLine2EdgeCount(floorJoist); i < l; i++) {
        const floorJoistRail = polyLine2Edge(floorJoist, i);
        polyLine2Extend(floorJoistRail, -DIST_Z_FIGHT, -DIST_Z_FIGHT);
        addJoist(floorPieces, JoistPlacement.FLOOR, floorJoistRail, floorPlateElevation - floorLayoutProfile.width);
      }
    }
  }

  if (useAssemblyProfiles) {
    floorFraming.push({
      joists: rimPieces,
      extrusionFramingProfile: getFramingProfileDescriptor(rimComponent),
    });
    floorFraming.push({
      joists: floorPieces,
      extrusionFramingProfile: getFramingProfileDescriptor(structureComponent),
    });
    if (lumberCounters) {
      updateLumberCounters(lumberCounters, rimPieces, false, findJoistLumberLength);
      updateLumberCounters(lumberCounters, floorPieces, false, findJoistLumberLength);
    }
  } else {
    const joists = rimPieces;
    floorFraming.push({ joists });
    lumberCounters && updateLumberCounters(lumberCounters, joists, false, findJoistLumberLength);
  }

  return floorFraming;
}

function addUniqueJoistRayOriginPoints(
  a: JoistRayOriginPoints[],
  b: JoistRayOriginPoints,
  tolsqrd: number = DIST_EPSILON_SQUARED,
) {
  let include: boolean = true;
  for (let i = 0; i < a.length; i++) {
    const dx = a[i].x - b.x;
    const dy = a[i].y - b.y;
    if (dx * dx + dy * dy < tolsqrd) {
      include = false;
      break;
    }
  }
  if (include) {
    a.push(b);
  }
  return a;
}

function groupFramingOutlines(framingOutlines: PolyLine2[]): PolyLine2[][] {
  const groupedFramingOutlines: PolyLine2[][] = [];
  const visited: boolean[] = Array<boolean>(framingOutlines.length).fill(false);
  let iInsidej: boolean = false;
  //to be optimized. a lot of redundant checks but it works for now
  for (let i = 0; i < framingOutlines.length; i++) {
    const framingOutline1 = framingOutlines[i];
    const groupedFramingOutline: PolyLine2[] = [];
    iInsidej = false;
    for (let j = 0; j < framingOutlines.length; j++) {
      if (j === i || visited[j]) {
        continue;
      }
      const framingOutline2 = framingOutlines[j];
      const containment1 = classifyContainmentPolyLine2PolyLine2(framingOutline1, framingOutline2);
      if (containment1 === PolyLine2PolyLine2ContainmentClass.INSIDE) {
        iInsidej = true;
        break;
      }
      const containment2 = classifyContainmentPolyLine2PolyLine2(framingOutline2, framingOutline1);
      if (containment2 === PolyLine2PolyLine2ContainmentClass.INSIDE) {
        //inside openings are always CW
        groupedFramingOutline.push(polyLine2SetCW(framingOutline2));
        visited[j] = true;
      }
    }
    if (!visited[i] && !iInsidej) {
      //outside framing is always CCW
      groupedFramingOutline.unshift(polyLine2SetCCW(framingOutline1));
      visited[i] = true;
      groupedFramingOutlines.push(groupedFramingOutline);
    }
  }
  return groupedFramingOutlines;
}

function computeFloorJoists(
  optimalBB: RPBB2,
  rayOriginPts: JoistRayOriginPoints[],
  spacing: number,
  height: number,
  width: number,
): PolyLine2[] {
  const refinedRayOriginPts: JoistRayOriginPoints[] = [];
  const floorJoists: PolyLine2[] = [];
  let joistSpanInXDir: boolean = false;
  //1. compute interim critical points
  for (let i = 0; i < rayOriginPts.length - 1; i++) {
    const pl1 = rayOriginPts[i];
    const pl2 = rayOriginPts[i + 1];
    joistSpanInXDir = pl1.spansInXDir;
    let lastDone: boolean = false;
    let originX: number = pl1.x;
    let originY: number = pl1.y;

    //add first critical point
    addUniqueJoistRayOriginPoints(refinedRayOriginPts, pl1);
    while (!lastDone) {
      //the calculated interim critical point origin should not go beyond the next critical point
      //TODO: spacing algorithm IMO needs to be improved for cases where the spacing is not a multiple of the joist height.
      //currently it adjusts at the end but we should do it at both ends, should be easy to do.
      if (joistSpanInXDir) {
        originY += spacing;
        if (originY < pl2.y - DIST_EPSILON && originY >= pl2.y - 0.75 * height) {
          originY = 0.5 * (originY - spacing + pl2.y);
        } else if (originY >= pl2.y) {
          originY = pl2.y;
          lastDone = true;
        }
      } else {
        originX += spacing;
        if (originX < pl2.x - DIST_EPSILON && originX >= pl2.x - 0.75 * height) {
          originX = 0.5 * (originX - spacing + pl2.x);
        } else if (originX >= pl2.x) {
          originX = pl2.x;
          lastDone = true;
        }
      }
      addUniqueJoistRayOriginPoints(refinedRayOriginPts, { x: originX, y: originY, spansInXDir: joistSpanInXDir });
    }
  }
  function segmentCoincidentOnAnyPolyEdge(x1: number, y1: number, x2: number, y2: number, pl: PolyLine2) {
    for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
      const edge = polyLine2Edge(pl, i);
      if (
        /*moving to a bigger tolerance and should be ok for the coincident segment cases - nothing should be this close 
        to the polyedge anyways*/
        vec2IsBetweenUsingLengthBasedCalc(edge[0], edge[1], edge[2], edge[3], x1, y1, COMPACTNESS_EPSILON) &&
        vec2IsBetweenUsingLengthBasedCalc(edge[0], edge[1], edge[2], edge[3], x2, y2, COMPACTNESS_EPSILON)
      ) {
        return true;
      }
    }
    return false;
  }
  //the below framing calculation is based on the assumption that the openings are sorted left to right or bottom
  //to top depending upon the joist span direction.
  //this approach will break in situations where the openings may not be sorted in the above manner i.e. imagine in a
  //plan there is a L shaped opening and there is another opening on the inside of the inner corner of the L shape.
  //In this situation the openings are not sorted in the above manner and the framing calculation will break.
  //TODO: fix the above issue
  function sortOpenings(a: PolyLine2, b: PolyLine2) {
    const aabbOfa = aabb2ByPolyLine2s([a]);
    const aabbOfb = aabb2ByPolyLine2s([b]);

    if (aabbOfa && aabbOfb) {
      if (joistSpanInXDir) {
        return aabbOfa.minX - aabbOfb.minX;
      } else {
        return aabbOfa.minY - aabbOfb.minY;
      }
    }
    return 0;
  }
  const rotOpeningsCopy = optimalBB.rotOpenings.slice();
  rotOpeningsCopy.sort(sortOpenings);

  const slightlyBiggerOuterPoly = offsetPolyLine2(optimalBB.rotPoly, SLIGHTLY_LESS_THAN_HALF * COMPACTNESS_EPSILON);

  //2. create interim joists to fit along the rim and also rotate to match the plan
  for (let i = 0; i < refinedRayOriginPts.length; i++) {
    const linePolyInters = intersectRay2PolyLine2(
      optimalBB.rotPoly,
      refinedRayOriginPts[i].x,
      refinedRayOriginPts[i].y,
      joistSpanInXDir ? 1 : 0,
      joistSpanInXDir ? 0 : 1,
    );
    ray2PolyLine2IntersectionMakeUnique(linePolyInters);
    const wt1: number = 1.0 - COMPACTNESS_EPSILON;
    const wt2: number = COMPACTNESS_EPSILON;

    for (let j = 0; j < linePolyInters.length - 1; j++) {
      const { x: x1, y: y1 } = linePolyInters[j];
      const { x: x2, y: y2 } = linePolyInters[j + 1];

      const contBeg = classifyContainmentVec2PolyLine2(
        wt1 * x1 + wt2 * x2,
        wt1 * y1 + wt2 * y2,
        slightlyBiggerOuterPoly,
      );
      const contEnd = classifyContainmentVec2PolyLine2(
        wt1 * x2 + wt2 * x1,
        wt1 * y2 + wt2 * y1,
        slightlyBiggerOuterPoly,
      );

      if (contBeg === Vec2PolyLine2ContainmentClass.INSIDE && contEnd === Vec2PolyLine2ContainmentClass.INSIDE) {
        const segments: PolyLine2[] = [];
        let xstart: number | undefined;
        let ystart: number | undefined;
        let xlast: number | undefined;
        let ylast: number | undefined;
        const pl = polyLine2ByTwoPoints(x1, y1, x2, y2);
        let reverse: boolean = false;
        if (joistSpanInXDir) {
          if (x1 > x2) {
            reverse = true;
          }
        } else {
          if (y1 > y2) {
            reverse = true;
          }
        }
        const plCorrected = reverse ? polyLine2ReverseCopy(pl) : pl;

        xstart = plCorrected[0];
        ystart = plCorrected[1];

        for (const opening of rotOpeningsCopy) {
          const lineOpeningInters = intersectRay2PolyLine2(
            opening,
            xstart,
            ystart,
            joistSpanInXDir ? 1 : 0,
            joistSpanInXDir ? 0 : 1,
          );
          ray2PolyLine2IntersectionMakeUnique(lineOpeningInters);

          for (let j = 0; j < lineOpeningInters.length - 1; j++) {
            const { x: x3, y: y3 } = lineOpeningInters[j];
            const { x: x4, y: y4 } = lineOpeningInters[j + 1];

            xlast = x4;
            ylast = y4;

            if (j === 0) {
              segments.push(polyLine2ByTwoPoints(xstart, ystart, x3, y3));
            }

            xstart = xlast;
            ystart = ylast;

            const contOpenBeg = classifyContainmentVec2PolyLine2(wt1 * x3 + wt2 * x4, wt1 * y3 + wt2 * y4, opening);
            const contOpenEnd = classifyContainmentVec2PolyLine2(wt1 * x4 + wt2 * x3, wt1 * y4 + wt2 * y3, opening);

            if (
              contOpenBeg === Vec2PolyLine2ContainmentClass.INSIDE &&
              contOpenEnd === Vec2PolyLine2ContainmentClass.INSIDE
            ) {
              continue;
            }
            if (
              contOpenBeg === Vec2PolyLine2ContainmentClass.COINCIDENT &&
              contOpenEnd === Vec2PolyLine2ContainmentClass.COINCIDENT
            ) {
              continue;
            }

            segments.push(polyLine2ByTwoPoints(x3, y3, x4, y4));
          }
        }
        if (segments.length > 0) {
          if (
            xlast !== undefined &&
            ylast !== undefined &&
            ((joistSpanInXDir && xlast < x2) || (!joistSpanInXDir && ylast < y2))
          ) {
            segments.push(polyLine2ByTwoPoints(xlast, ylast, x2, y2));
          }
        } else {
          segments.push(plCorrected);
        }

        for (const segment of segments) {
          //too small do not add
          if (polyLine2Length(segment) < 2 * width) {
            continue;
          }

          //coincident with initial framing loop so do not add
          if (segmentCoincidentOnAnyPolyEdge(segment[0], segment[1], segment[2], segment[3], optimalBB.rotPoly)) {
            continue;
          }

          //ignore nans
          //if(polyLine2FindNaN(segment)>=0) {console.log(segment);continue;}

          if (joistSpanInXDir) {
            polyLine2Move(segment, 0, -0.5 * width);
          } else {
            polyLine2Move(segment, 0.5 * width, 0);
          }
          polyLine2ExtendStart(segment, -0.5 * width);
          polyLine2ExtendEnd(segment, -0.5 * width);

          polyLine2Rotate(segment, optimalBB.rot, 0, 0);
          floorJoists.push(segment);
        }
      }
    }
  }
  return floorJoists;
}
// this function calculates directional bounding lines for framing calc
function computeJoistsRayOriginPointsForFramingCalc(
  optimalBB: RPBB2,
  spanDir: SpanDirection = SpanDirection.SHORTEST_SPAN,
): JoistRayOriginPoints[] {
  const BIG_NUMBER = 1e10;
  //1. compute joist lines based on requested span direction
  const diffX = optimalBB.maxX - optimalBB.minX;
  const diffY = optimalBB.maxY - optimalBB.minY;
  let spansInXDir: boolean = false;
  if (
    (diffX < diffY && spanDir === SpanDirection.SHORTEST_SPAN) ||
    (diffX > diffY && spanDir === SpanDirection.LONGEST_SPAN)
  ) {
    spansInXDir = true;
  }

  const criticalPts: JoistRayOriginPoints[] = [];

  // add bounding polygon vertices as origin points
  for (let i = 0; i < polyLine2VertexCount(optimalBB.rotPoly) - 1; i++) {
    const [x, y] = polyLine2Vertex(optimalBB.rotPoly, i);
    criticalPts.push({ x: spansInXDir ? -BIG_NUMBER : x, y: spansInXDir ? y : -BIG_NUMBER, spansInXDir });
  }
  // add openings vertices as origin points
  for (const opening of optimalBB.rotOpenings) {
    for (let i = 0; i < polyLine2VertexCount(opening) - 1; i++) {
      const [x, y] = polyLine2Vertex(opening, i);
      criticalPts.push({ x: spansInXDir ? -BIG_NUMBER : x, y: spansInXDir ? y : -BIG_NUMBER, spansInXDir });
    }
  }
  // function to sort critical points from -x to +x or -y to +y
  function sortOriginPoints(a: JoistRayOriginPoints, b: JoistRayOriginPoints) {
    let diff = a.x - b.x;
    if (spansInXDir) {
      diff = a.y - b.y;
    }
    return diff;
  }
  // return only unique critical points
  return criticalPts.sort(sortOriginPoints).reduce(addUniqueJoistRayOriginPoints, []);
}

function computeOptimalBoundingBox(polyline: PolyLine2[], resize: number = 0): RPBB2 {
  //checking with absolute zero
  const pl =
    resize === 0 ? polyLine2CopySimplify(polyline[0]) : polyLine2CopySimplify(offsetPolyLine2(polyline[0], -resize));
  const openings = polyline.slice(1);
  const polyArea = polyLine2Area(pl);
  let aabb = aabb2ByPolyLine2s([pl]);
  assertIsNotNullOrUndefined(aabb);
  let diffArea = aabb2Area(aabb) - polyArea;
  // set data assuming it is axis aligned
  let rpbb: RPBB2 = {
    ...aabb,
    rot: 0,
    minusArea: diffArea,
    rotPoly: pl,
    rotOpenings: openings.map((o) => polyLine2CopySimplify(o)),
  };

  if (!polyLine2IsAxisAlignedRect(pl)) {
    //1. Compute optimal rotation angle which minimizes area between bounding box and the polygon
    //2. In this implementation, it uses only edge angles for adjustments i.e. assumes minimum diff area would be
    //   found along these angles. This may only be true for axis aligned polygons or rotated polygons with perpendicular
    //   edges. Overall, a more robust calc may be required via some iterative scheme. I will consider this approach for later.
    for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
      const edge = polyLine2Edge(pl, i);
      const edgeAngle = Math.atan2(edge[3] - edge[1], edge[2] - edge[0]);
      const plnew = polyLine2Rotate(polyLine2Copy(pl), -edgeAngle, 0, 0);
      aabb = aabb2ByPolyLine2s([plnew]);
      assertIsNotNullOrUndefined(aabb);
      diffArea = aabb2Area(aabb) - polyArea;

      const rotOpenings: PolyLine2[] = [];
      for (const opening of openings) {
        const resizedOpening =
          resize === 0 ? polyLine2CopySimplify(opening) : polyLine2CopySimplify(offsetPolyLine2(opening, resize));
        const rotOpening = polyLine2Rotate(resizedOpening, -edgeAngle, 0, 0);
        rotOpenings.push(rotOpening);
      }

      if (diffArea < rpbb.minusArea - DIST_EPSILON) {
        rpbb = { ...aabb, rot: edgeAngle, minusArea: diffArea, rotPoly: plnew, rotOpenings };
      }
    }
  }
  return rpbb;
}
